import React from "react";
import { MapStateToPropsParam } from "react-redux";

import { StrollerReviews } from "@/mk2/apps/strollers/containers/StrollerReviews/StrollerReviews";
import { strollerReviewsFetchTrigger } from "@/mk2/apps/strollers/containers/StrollerReviews/StrollerReviews.actions";
import { strollersReviewsReducer } from "@/mk2/apps/strollers/containers/StrollerReviews/StrollerReviews.reducers";
import saga from "@/mk2/apps/strollers/containers/StrollerReviews/StrollerReviews.sagas";
import { getStrollerReviewsState } from "@/mk2/apps/strollers/containers/StrollerReviews/StrollerReviews.selectors";
import { breadcrumbsStroller, productSchemaStroller } from "@/mk2/apps/strollers/structuredData";
import { strollersAddReviewUrl, strollersDetailUrl } from "@/mk2/apps/strollers/urls";
import { Btn, BtnType } from "@/mk2/components/Btn";
import { menuToggle } from "@/mk2/containers/Menu/Menu.actions";
import { MapDispatchToPropsObject } from "@/mk2/helpers/types";
import { url } from "@/mk2/helpers/urls";
import Page, { HalfpageProps, PageRouteProps } from "@/mk2/pages/Page";
import { AppState, getRequestBaseUrl, getRequestPermissions } from "@/mk2/reducers";
import { LoadingState, isLoading } from "@/mk2/reducers/loadingState";
import {
    AST,
    ProductEntity,
    ProductSchema,
    StrollerEntity,
    StrollerSchema,
    StrollerUserReviewEntity,
    StrollerUserReviewSchema,
} from "@/mk2/schemas";
import { getDenormalizedEntities, getDenormalizedEntity } from "@/mk2/selectors";
import { interpolate } from "@/mk2/services/i18n";
import { Store } from "@/mk2/store/configureStore";
import {
    SEO_TITLE_STROLLER_REVIEWS_stroller,
    STROLLER_ADD_REVIEW,
    STROLLER_SEO_GENERAL_DESC,
} from "@/mk/autogenerated/translation";
import { canManageStrollers } from "@/mk/bazaar/common/userUtils";

export const STROLLER_REVIEW_MENU_MORE = (id: number) => `stroller-review-menu-more-${id}`;

interface OwnProps {}

interface StateProps {
    baseUrl: string;
    loadingState: LoadingState;
    strollerSlug: string;
    stroller: StrollerEntity;
    descriptionAST: AST;
    strollerReviews: StrollerUserReviewEntity[]; // schvalene recenzie kocika 'stroller' alebo jeho pribuznych modelov
    strollerPendingReviews: StrollerUserReviewEntity[]; // recenzie kocika 'stroller' cakajuce na schvalenie
    strollerAlikeStrollers: StrollerEntity[];
    strollerReviewsActiveProducts: ProductEntity[];
    isAdmin: boolean;
}

interface DispatchProps {
    onLoad(slug: string);
    onToggleMenuMore(reviewId: number);
}

type RouteProps = PageRouteProps<{ slug: string }>;

export type Props = OwnProps & StateProps & DispatchProps & RouteProps;

export default class StrollerReviewsPage extends Page<
    OwnProps & RouteProps,
    StateProps,
    DispatchProps
> {
    public mapStateToProps(): MapStateToPropsParam<StateProps, OwnProps & RouteProps, AppState> {
        return (state: AppState, ownProps: OwnProps & RouteProps): StateProps => {
            const slug = ownProps.match.params.slug;
            const reviewsState = getStrollerReviewsState(state, slug);

            const stroller = getDenormalizedEntity<StrollerEntity>(
                state,
                StrollerSchema,
                reviewsState.strollerId,
            );
            const descriptionAST = reviewsState.descriptionAST;
            const strollerReviews = getDenormalizedEntities<StrollerUserReviewEntity>(
                state,
                StrollerUserReviewSchema,
                reviewsState.strollerReviewIds,
            );
            const strollerPendingReviews = getDenormalizedEntities<StrollerUserReviewEntity>(
                state,
                StrollerUserReviewSchema,
                reviewsState.strollerPendingReviewIds,
            );
            const strollerAlikeStrollers = getDenormalizedEntities<StrollerEntity>(
                state,
                StrollerSchema,
                reviewsState.strollerAlikeStrollerIds,
            );
            const strollerReviewsActiveProducts = getDenormalizedEntities<ProductEntity>(
                state,
                ProductSchema,
                reviewsState.strollerReviewsActiveProductIds,
            );

            const requestUserPermissions = getRequestPermissions(state);
            const isAdmin = canManageStrollers(requestUserPermissions);

            return {
                baseUrl: getRequestBaseUrl(state),
                loadingState: reviewsState.loadingState,
                strollerSlug: slug,
                stroller,
                descriptionAST,
                strollerReviews,
                strollerPendingReviews,
                strollerAlikeStrollers,
                strollerReviewsActiveProducts,
                isAdmin,
            };
        };
    }

    public mapDispatchToProps(): MapDispatchToPropsObject<DispatchProps> {
        return {
            onLoad: strollerReviewsFetchTrigger,
            onToggleMenuMore: (reviewId: number) => menuToggle(STROLLER_REVIEW_MENU_MORE(reviewId)),
        };
    }

    public getMetaTitle(props: Props): string {
        const { stroller } = props;
        return stroller
            ? interpolate(SEO_TITLE_STROLLER_REVIEWS_stroller, { stroller: stroller.name })
            : null;
    }

    public getMetaDescription(props: Props): string {
        const { stroller } = props;
        if (stroller) {
            return `${stroller.name} - ${STROLLER_SEO_GENERAL_DESC}`;
        }
    }

    public render(props: Props): false | JSX.Element {
        return <StrollerReviews {...props} />;
    }

    public getHeaderRight(props: Props): React.ReactElement {
        return (
            <Btn
                type={BtnType.Blue}
                label={STROLLER_ADD_REVIEW}
                link={url(strollersAddReviewUrl, { slug: props.strollerSlug })}
            />
        );
    }

    public hasHeaderBackButton(props: Props): boolean {
        return true;
    }

    public getDefaultBackPath(props: Props): string {
        return url(strollersDetailUrl, { slug: props.strollerSlug });
    }

    public getHalfpageProps(props: Props): HalfpageProps {
        return {
            zone: "Strollers Index - Halfpage Sticky (Desktop)",
        };
    }

    public registerInStore(store: Store<AppState>) {
        store.injectReducer("containers.strollers.strollersReviews", strollersReviewsReducer);
        store.injectSaga("containers.strollers.strollersReviews", saga);
    }

    public getStructuredData(props: Props): object[] {
        const { stroller, descriptionAST, strollerReviews, baseUrl, loadingState } = props;
        if (!stroller || isLoading(loadingState)) {
            return [];
        }
        const data: object[] = [breadcrumbsStroller(baseUrl, stroller)];
        // https://developers.google.com/search/docs/appearance/structured-data/product#shopping-experiences
        // we want here result type "Product snippets in search results"
        data.push(
            productSchemaStroller(baseUrl, stroller, descriptionAST, strollerReviews, [], null),
        );
        return data;
    }
}
